import { faCalendar } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import { useParams, type To } from 'react-router';
import styled from 'styled-components';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Lightning } from '@sorare/core/src/atoms/icons/Lightning';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { useLastVisitedEventType } from '#src/hooks/useLastVisitedEventType.ts';
import { isClassicOnlyEventTypeSport } from '#src/lib/so5.ts';

const Root = styled(Horizontal)`
  align-self: center;

  @media ${laptopAndAbove} {
    padding-top: 0;
    align-self: start;
  }
`;

type Props = {
  getTo?: (eventType: Lowercase<So5FixtureEvent>) => To;
  onClick?: (eventType: So5FixtureEvent) => void;
};
export const EventTypeSwitcher = ({ onClick, getTo }: Props) => {
  const { sport } = useSportContext();
  const { eventType: eventTypeFromPath } = useParams();
  const { setLastVisitedEventType } = useLastVisitedEventType();
  const uppercasedLastVisitedEventType = eventTypeFromPath?.toUpperCase();

  const handleClick = (eventType: So5FixtureEvent) => {
    onClick?.(eventType);
    setLastVisitedEventType(eventType);
  };

  if (isClassicOnlyEventTypeSport(sport)) {
    return null;
  }

  return (
    <Root>
      <Button
        size="small"
        color={
          uppercasedLastVisitedEventType === So5FixtureEvent.DAILY
            ? 'quaternary'
            : 'tertiary'
        }
        startIcon={<Lightning color="var(--c-score-high)" />}
        onClick={() => handleClick(So5FixtureEvent.DAILY)}
        to={getTo?.('daily')}
      >
        <FormattedMessage {...glossary.daily} />
      </Button>
      <Button
        size="small"
        color={
          uppercasedLastVisitedEventType === So5FixtureEvent.CLASSIC
            ? 'quaternary'
            : 'tertiary'
        }
        startIcon={
          <FontAwesomeIcon
            icon={faCalendar}
            color="var(--c-score-mediumHigh)"
          />
        }
        to={getTo?.('classic')}
        onClick={() => handleClick(So5FixtureEvent.CLASSIC)}
      >
        <FormattedMessage {...glossary.biweekly} />
      </Button>
    </Root>
  );
};
